import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
  Business,
  Person,
  DateRange,
  Payment,
  Receipt,
  Download,
  PictureAsPdf,
  Close,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPeppolInvoiceByUniqueId, downloadPeppolAttachment } from '../services/invoiceService';

const PeppolInvoiceView = () => {
  const { t } = useTranslation();
  const { uniqueId } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfPreview, setPdfPreview] = useState({ open: false, url: null, filename: null, loading: false });

  useEffect(() => {
    fetchInvoiceDetails();
  }, [uniqueId]);

  const fetchInvoiceDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getPeppolInvoiceByUniqueId(uniqueId);
      if (response.success) {
        setInvoice(response.data);
      } else {
        setError(response.message || 'Failed to fetch invoice details');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching invoice details');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPdf = async () => {
    setPdfPreview((prev) => ({ ...prev, loading: true }));
    try {
      const response = await downloadPeppolAttachment(uniqueId);
      const contentDisposition = response.headers?.['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=([^;\n]*)/);
      const filename = filenameMatch ? filenameMatch[1].replace(/["\']/g, '').trim() : `invoice-${uniqueId}.pdf`;
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfPreview({ open: true, url, filename, loading: false });
    } catch (err) {
      setPdfPreview({ open: false, url: null, filename: null, loading: false });
    }
  };

  const handleClosePdfPreview = () => {
    if (pdfPreview.url) {
      URL.revokeObjectURL(pdfPreview.url);
    }
    setPdfPreview({ open: false, url: null, filename: null, loading: false });
  };

  const handleDownloadPdf = () => {
    if (!pdfPreview.url) return;
    const a = document.createElement('a');
    a.href = pdfPreview.url;
    a.download = pdfPreview.filename || `invoice-${uniqueId}.pdf`;
    a.click();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const paymentMethod = (
    invoice?.paymentMethod ||
    invoice?.PaymentMethod ||
    invoice?.payment?.method ||
    invoice?.payment?.type ||
    invoice?.paymentType ||
    '-'
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#f8f9fa',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/sales/received-peppol-invoices')}
          sx={{ mt: 2 }}
        >
          Back to Invoices
        </Button>
      </Container>
    );
  }

  if (!invoice) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">Invoice not found</Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/sales/received-peppol-invoices')}
          sx={{ mt: 2 }}
        >
          Back to Invoices
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f9fa', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/sales/received-peppol-invoices')}
            sx={{
              mb: 2,
              color: '#667eea',
              '&:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.08)',
              },
            }}
          >
            Back to Invoices
          </Button>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                Invoice Details
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Invoice No: {invoice.invoiceNo}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {invoice.hasAttachment && (
                <Button
                  variant="outlined"
                  startIcon={pdfPreview.loading ? <CircularProgress size={18} /> : <PictureAsPdf />}
                  onClick={handleViewPdf}
                  disabled={pdfPreview.loading}
                  sx={{ textTransform: 'none' }}
                >
                  {pdfPreview.loading ? 'Loading...' : 'View PDF'}
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/* Invoice Overview Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                height: '100%',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Business sx={{ color: '#667eea', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Supplier Information
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  {invoice.supplier.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  <strong>VAT:</strong> {invoice.supplier.vatNumber}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  <strong>Identifier:</strong> {invoice.supplier.identifier}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Address:</strong> {invoice.supplier.address}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                height: '100%',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Person sx={{ color: '#667eea', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Customer Information
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  {invoice.customer.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  <strong>VAT:</strong> {invoice.customer.vatNumber}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  <strong>Identifier:</strong> {invoice.customer.identifier}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Address:</strong> {invoice.customer.address}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Invoice Details */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            mb: 4,
          }}
        >
          <Box sx={{ p: 3, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Invoice Information
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Receipt sx={{ fontSize: 20, color: '#667eea', mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Issue Date
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {formatDate(invoice.issueDate)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <DateRange sx={{ fontSize: 20, color: '#667eea', mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Due Date
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {formatDate(invoice.dueDate)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Payment sx={{ fontSize: 20, color: '#667eea', mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Currency
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {invoice.currencyCode}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Receipt sx={{ fontSize: 20, color: '#667eea', mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Buyer Reference
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {invoice.buyerReference}
                </Typography>
              </Grid>
              {invoice.billingPeriod && (
                <>
                  <Grid item xs={12} sm={6} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <DateRange sx={{ fontSize: 20, color: '#667eea', mr: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Billing Period
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatDate(invoice.billingPeriod.start)} -{' '}
                      {formatDate(invoice.billingPeriod.end)}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
            {invoice.notes && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Notes
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    backgroundColor: '#f8f9fa',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body2">{invoice.notes}</Typography>
                </Paper>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Payment Information */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            mb: 4,
          }}
        >
          <Box sx={{ p: 3, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Payment Information
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Payment Type
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {paymentMethod}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Payment Identifier
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {invoice.payment.identifier}
                </Typography>
              </Grid>
              {invoice.payment.termsNote && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Payment Terms
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      backgroundColor: '#f8f9fa',
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2">{invoice.payment.termsNote}</Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Box>
        </Paper>

        {/* Line Items */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            mb: 4,
          }}
        >
          <Box sx={{ p: 3, backgroundColor: 'rgba(102, 126, 234, 0.08)' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Line Items
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Quantity
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Amount excl. VAT
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Tax %
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Tax Amount
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoice.lineItems.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{item.lineNumber}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {item.quantity}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(item.taxableAmount, invoice.currencyCode)}
                    </TableCell>
                    <TableCell align="right">{item.taxPercentage}%</TableCell>
                    <TableCell align="right">
                      {formatCurrency(item.taxAmount, invoice.currencyCode)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatCurrency(item.lineTotal, invoice.currencyCode)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Totals */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          }}
        >
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: '#f8f9fa',
                    borderRadius: 2,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Total Excluding VAT
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#667eea' }}>
                    {formatCurrency(invoice.amounts.totalExcludingVAT, invoice.currencyCode)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: '#f8f9fa',
                    borderRadius: 2,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Total VAT
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#667eea' }}>
                    {formatCurrency(invoice.amounts.totalVAT, invoice.currencyCode)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: '#f8f9fa',
                    borderRadius: 2,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Prepaid Amount
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#ff9800' }}>
                    {formatCurrency(invoice?.amounts?.prepaidAmount=== undefined ? 0 : invoice.amounts.prepaidAmount, invoice.currencyCode)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    p: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 2,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="body2" sx={{ mb: 1, color: '#fff' }}>
                    Payable Amount
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#fff' }}>
                    {formatCurrency(invoice.amounts.payableAmount, invoice.currencyCode)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </Box>

      {/* PDF Preview Dialog */}
      <Dialog
        open={pdfPreview.open}
        onClose={handleClosePdfPreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            height: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 2,
            px: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PictureAsPdf sx={{ fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {pdfPreview.filename || 'Invoice PDF'}
            </Typography>
          </Box>
          <IconButton onClick={handleClosePdfPreview} sx={{ color: '#fff' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ flex: 1, p: 0, overflow: 'hidden' }}>
          {pdfPreview.url && (
            <iframe
              src={pdfPreview.url}
              title="PDF Preview"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <Button
            onClick={handleClosePdfPreview}
            variant="outlined"
            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
          >
            Close
          </Button>
          <Button
            onClick={handleDownloadPdf}
            variant="contained"
            startIcon={<Download />}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              fontWeight: 600,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)',
              },
            }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PeppolInvoiceView;
